import type { FilterPropertyOption } from "@/components/table";
import { api } from "@/services/api";
import type { CarrierInfo } from "@/types/api";

const SUGGESTION_LIMIT = 20;

/** Deduplicated registry row used for carrier filter suggestions. */
export interface CanonicalCarrierOption {
  code: string;
  name: string;
  scac: string;
}

/**
 * Deduplicate registry rows by canonical code (falling back to SCAC), keeping
 * the first full name seen. Inactive rows are skipped.
 */
export function dedupeActiveCarriers(
  carriers: readonly CarrierInfo[],
): CanonicalCarrierOption[] {
  const seen = new Map<string, CanonicalCarrierOption>();
  for (const carrier of carriers) {
    if (!carrier.is_active) continue;
    const code = (carrier.canonical_code || carrier.scac).trim().toUpperCase();
    if (!code || seen.has(code)) continue;
    seen.set(code, {
      code,
      name: carrier.full_name.trim() || code,
      scac: carrier.scac.trim().toUpperCase(),
    });
  }
  return Array.from(seen.values());
}

/** Filter registry carriers by code, SCAC, or full name. */
export function filterCarrierOptions(
  carriers: readonly CarrierInfo[],
  query: string,
): FilterPropertyOption[] {
  const needle = query.trim().toLowerCase();
  const options: FilterPropertyOption[] = [];
  for (const carrier of dedupeActiveCarriers(carriers)) {
    const label =
      carrier.name && carrier.name !== carrier.code
        ? `${carrier.code} — ${carrier.name}`
        : carrier.code;
    if (
      needle &&
      !carrier.code.toLowerCase().includes(needle) &&
      !carrier.scac.toLowerCase().includes(needle) &&
      !carrier.name.toLowerCase().includes(needle)
    ) {
      continue;
    }
    options.push({ value: carrier.code, label });
    if (options.length >= SUGGESTION_LIMIT) break;
  }
  return options;
}

/** Merge extra options (e.g. distinct booking values) after registry hits. */
export function mergeCarrierOptions(
  primary: readonly FilterPropertyOption[],
  extras: readonly FilterPropertyOption[],
): FilterPropertyOption[] {
  const seen = new Set(primary.map((option) => option.value.toUpperCase()));
  const merged = [...primary];
  for (const option of extras) {
    const key = option.value.trim().toUpperCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    merged.push({
      value: option.value,
      label: option.label || option.value,
    });
    if (merged.length >= SUGGESTION_LIMIT) break;
  }
  return merged;
}

/** Map typed code or SCAC to the registry canonical code (or uppercased input). */
export function resolveCanonicalCarrierCode(
  raw: string,
  carriers: readonly CanonicalCarrierOption[],
): string {
  const key = raw.trim().toUpperCase();
  if (!key) return key;
  for (const carrier of carriers) {
    if (carrier.code === key || carrier.scac === key) return carrier.code;
  }
  return key;
}

/** Load carrier suggestions from the cached registry, filtered by `query`. */
export async function loadCarrierFilterOptions(
  query: string,
  extras: readonly FilterPropertyOption[] = [],
): Promise<FilterPropertyOption[]> {
  try {
    const carriers = await api.getCarriersRegistry();
    return mergeCarrierOptions(filterCarrierOptions(carriers, query), extras);
  } catch {
    return mergeCarrierOptions([], extras);
  }
}
